import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useState,
  type ReactNode,
} from 'react';
import { useQuery } from '@tanstack/react-query';
import { queryClient } from '@/shared/lib/queryClient';
import { apiClient, setTenantIdProvider } from '@/shared/lib/apiClient';
import { useAuth } from '@/modules/auth/hooks/useAuth';
import { isElevatedUser } from '@/modules/auth/lib/userMode';
import type { ApiSuccessResponse } from '@super-admin/shared-types';

const STORAGE_KEY = 'tenant.activeId';

export interface TenantSummary {
  id: string;
  name: string;
  display_name: string;
  logo_url: string | null;
  status: string;
  slug: string;
}

export interface TenantContextValue {
  activeTenant: TenantSummary | null;
  activeTenantId: string | null;
  setActiveTenant: (id: string) => void;
  availableTenants: TenantSummary[];
  isElevated: boolean;
  isLoading: boolean;
}

const TenantContext = createContext<TenantContextValue | null>(null);

export function TenantProvider({ children }: { children: ReactNode }) {
  const { user } = useAuth();
  const [activeId, setActiveId] = useState<string | null>(() =>
    typeof sessionStorage !== 'undefined' ? sessionStorage.getItem(STORAGE_KEY) : null,
  );

  const isElevated = useMemo(() => isElevatedUser(user), [user]);

  const { data: availableTenants = [], isLoading } = useQuery({
    queryKey: ['me', 'available-tenants'],
    queryFn: () =>
      apiClient
        .get<ApiSuccessResponse<TenantSummary[]>>('/api/v1/me/available-tenants')
        .then((r) => r.data),
    enabled: !!user,
    staleTime: 60_000,
  });

  useEffect(() => {
    if (!user) {
      setActiveId(null);
      sessionStorage.removeItem(STORAGE_KEY);
      return;
    }
    if (!isElevated && user.tenantId) {
      setActiveId(user.tenantId);
    }
  }, [user, isElevated]);

  const setActiveTenant = useCallback((id: string) => {
    setActiveId(id);
    sessionStorage.setItem(STORAGE_KEY, id);
    queryClient.clear();
  }, []);

  const activeTenant = useMemo(() => {
    if (!activeId) return null;
    return availableTenants.find((t) => t.id === activeId) ?? null;
  }, [activeId, availableTenants]);

  useEffect(() => {
    setTenantIdProvider(() => activeId);
  }, [activeId]);

  const value = useMemo<TenantContextValue>(
    () => ({
      activeTenant,
      activeTenantId: activeId,
      setActiveTenant,
      availableTenants,
      isElevated,
      isLoading,
    }),
    [activeTenant, activeId, setActiveTenant, availableTenants, isElevated, isLoading],
  );

  return <TenantContext.Provider value={value}>{children}</TenantContext.Provider>;
}

// eslint-disable-next-line react-refresh/only-export-components
export function useTenant(): TenantContextValue {
  const ctx = useContext(TenantContext);
  if (!ctx) {
    throw new Error('useTenant must be used within TenantProvider');
  }
  return ctx;
}
